import styled from "styled-components";

import { MelcoModelsNotification } from "melco-shared-logic";

import { Button, Empty, List, Popover, Title } from "melco-ui";
import { NotificationListItem } from "./NotificationListItem";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash-es/isEmpty";
import { useDeleteAllNotifications } from "../../hooks/notifications/useDeleteAllNotifications";
import { NOTIFICATIONS_CACHE_KEY } from "../../hooks/notifications/useNotifications";
import { useQueryClient } from "react-query";
import { useConfirm } from "../../hooks/useConfirm";

const ListWrapper = styled.div`
  width: 300px;
  max-height: 60vh;
  max-height: 600px;
  overflow-y: auto;
`;

const DeleteAllNotificationsButton = styled(Button).attrs({
  type: "text",
  danger: true,
})`
  width: 100%;
`;

type NotificationPopoverProps = {
  notifications: MelcoModelsNotification[];
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
};

export const NOTIFICATIONS_POPOVER_CONFIRM_DOM_ID =
  "notifications-popover-confirm";

export const NotificationsPopover: React.FC<NotificationPopoverProps> = ({
  notifications,
  onOpenChange,
  children,
}) => {
  const { t } = useTranslation();
  const deleteAllNotifications = useDeleteAllNotifications();
  const queryClient = useQueryClient();

  const confirmDeleteAll = useConfirm(
    async () => {
      await deleteAllNotifications();
      queryClient.invalidateQueries(NOTIFICATIONS_CACHE_KEY);
    },
    {
      translationPrefix: "notifications.actions.delete_all",
      getContainer: () =>
        document.getElementById(NOTIFICATIONS_POPOVER_CONFIRM_DOM_ID)!,
    }
  );

  const popoverContent = (
    <div data-cy-id="notifications-popover-content">
      <Title level={5}>{t("notifications.title")}</Title>
      <ListWrapper>
        {isEmpty(notifications) ? (
          <Empty description={t("notifications.empty")} />
        ) : (
          <>
            <List
              dataSource={notifications}
              renderItem={(item) => (
                <NotificationListItem notification={item} />
              )}
            />
            <DeleteAllNotificationsButton
              onClick={() => confirmDeleteAll()}
              data-cy-id="notifications-delete-all"
            >
              {t("notifications.actions.delete_all.button")}
            </DeleteAllNotificationsButton>
          </>
        )}
      </ListWrapper>
      <div id={NOTIFICATIONS_POPOVER_CONFIRM_DOM_ID}></div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      placement="bottomRight"
      trigger="click"
      destroyTooltipOnHide
      onOpenChange={onOpenChange}
      zIndex={1000}
    >
      {children}
    </Popover>
  );
};
